import media from "../../Utils/Media";
import { clamp } from "../../Utils/Number";
import constants from "./Constants";

interface IScrollerNav {
    gap: number;
    horizontal: boolean;
    tiles: NodeListOf<HTMLElement>;
    current: number;
    prev: HTMLElement;
    next: HTMLElement;
    track: HTMLElement;
}

export type scrollerNavBeforeResultDetail = {
    deferNav?: number;
};

declare interface ScrollerNavBeforeResultEvent extends CustomEvent {
    detail: scrollerNavBeforeResultDetail;
}

class ScrollerNav implements IScrollerNav {
    private readonly node: HTMLElement;
    private _prev: HTMLElement;
    private _next: HTMLElement;
    private clickHandler: (e: PointerEvent) => void;
    private thumbHandler: (e: PointerEvent) => void;
    private scrollHandler: (e: Event) => void;
    private debounceScrollHandlerTimeout: ReturnType<typeof setTimeout>;
    private _moving: boolean = false;
    private _pos: number;
    private _style: CSSStyleDeclaration;
    private nodeOffset: number;
    private activeTile: number = 0;

    constructor(node: HTMLElement) {
        this.node = node;
        if (this.node.dataset.url) {
            this.node.addEventListener(constants.EVENTS.TILES_LOADED, this.init.bind(this));
        } else {
            window.addEventListener("DOMContentLoaded", this.init.bind(this), { once: true });
        }
    }

    private init() {
        if (
            this.thumbs ||
            !media.isTouchOnly ||
            (this.prev?.dataset.touch === "true" && this.next?.dataset.touch === "true")
        ) {
            this.clickHandler = this.handleClick.bind(this);
            this.thumbHandler = this.handleThumb.bind(this);
            this.scrollHandler = this.handleScroll.bind(this);
            this._pos = this.node.scrollLeft;
            this.hasNav?.updateOffsets().updateNavigation().addListeners();
            this.node.dispatchEvent(
                new CustomEvent(constants.EVENTS.SCROLL_NAV_READY, { detail: { handler: this, index: this.current } })
            );
        }
        this.initPreload();
    }

    /**
     * remove loading attribute if node intersects to preload all images in gallery
     * @private
     */
    private initPreload() {
        const observer = new IntersectionObserver(
            (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
                if (!entries[0].isIntersecting) return;
                observer.unobserve(this.node);
                this.allThumbs.forEach((t) => t.querySelector("img[loading]")?.removeAttribute("loading"));
                this.tiles.forEach((t) => t.querySelector("img[loading]")?.removeAttribute("loading"));
            },
            { rootMargin: "0px 0px 200px 0px" }
        );
        observer.observe(this.node);
    }

    private get hasNav(): ScrollerNav {
        return (this.prev !== null && this.next !== null) || this.thumbs.length ? this : undefined;
    }

    /**
     * obtain style declaration
     * @private
     */
    private get style(): CSSStyleDeclaration {
        if (!this._style) {
            this._style = getComputedStyle(this.node);
        }
        return this._style;
    }

    /**
     * obtain gap
     */
    get gap(): number {
        return parseInt(this.style.gap);
    }

    /**
     * determine if scroller scrolls horizontal
     */
    get horizontal(): boolean {
        return this.style.getPropertyValue("--overflow-x") === "auto";
    }

    /**
     * get tiles node list
     */
    get tiles(): NodeListOf<HTMLElement> {
        return this.node.querySelectorAll(constants.SELECTORS.TILES);
    }

    /**
     * obtain current tile index
     */
    get current(): number {
        // nodeOffset is always 0 for scrollers except gallery scrollers
        // assigning nodePadding when it's 0 fixed the issue
        const offset = this.nodeOffset + this.nodePadding;
        return [...this.tiles].findIndex((t) => {
            const rect = t.getBoundingClientRect();
            // truthy if left/top boundary is in between tiles bounds
            return rect.left - this.gap / 2 <= offset && rect.right + this.gap / 2 >= offset;
        });
    }

    /**
     * obtain prev nav node
     */
    get prev(): HTMLElement {
        if (!this._prev) {
            this._prev = this.node.parentNode.querySelector(constants.SELECTORS.PREV);
        }
        return this._prev;
    }

    /**
     * obtain next nav node
     */
    get next(): HTMLElement {
        if (!this._next) {
            this._next = this.node.parentNode.querySelector(constants.SELECTORS.NEXT);
        }
        return this._next;
    }

    get track(): HTMLElement {
        return this.node.parentNode.querySelector<HTMLElement>(constants.SELECTORS.TRACK);
    }

    /**
     * obtain thumbs array
     */
    get thumbs(): HTMLElement[] {
        return [...(this.track?.querySelectorAll<HTMLElement>(constants.SELECTORS.THUMBS) || [])];
    }

    /**
     * obtain thumbs array including hidden elements
     */
    get allThumbs(): HTMLElement[] {
        return [...(this.track?.querySelectorAll<HTMLElement>(constants.SELECTORS.THUMBS_WITH_HIDDEN) || [])];
    }

    /**
     * obtain offset of scroller node
     */
    get nodePadding(): number {
        return Math.round(
            parseFloat(this.style.getPropertyValue(`padding-${this.horizontal ? "inline" : "block"}-start`))
        );
    }

    /**
     * obtain scroll amount of scroller node
     */
    get scrollOffset(): number {
        return this.node[`scroll${this.horizontal ? "Left" : "Top"}`];
    }

    /**
     * defer click navigation in ms
     * @private
     */
    private get deferNav() {
        return Number(this.node.dataset.deferNav) || 0;
    }

    private set deferNav(value: number) {
        this.node.dataset.deferNav = String(value);
    }

    /**
     * determine if scroller reached start position
     */
    isAtStart(): boolean {
        return this.node[`scroll${this.horizontal ? "Left" : "Top"}`] === 0;
    }

    /**
     * determine if scroller reached end position
     */
    isAtEnd(): boolean {
        const scrollMax = this.node[`scroll${this.horizontal ? "Width" : "Height"}`];
        const current = this.node[`scroll${this.horizontal ? "Left" : "Top"}`];
        const dim = this.node[`offset${this.horizontal ? "Width" : "Height"}`];
        const pos = Math.round(scrollMax - current);
        const threshold = 4;
        for (let i = dim - threshold; i <= dim + threshold; i++) {
            if (pos === i) return true;
        }
        return false;
    }

    goto(index: number, behavior: ScrollBehavior = "smooth"): void {
        if (index < 0 || index > this.tiles.length - 1) return;
        const offset = this.calculateOffset(index);
        this.node.scrollTo({ behavior: behavior, ...offset });
        if (this.node.scrollLeft === offset.left && this.node.scrollTop === offset.top) {
            // in case if scrolling wasn't necessary we fire the scroll end event anyway
            this.node.dispatchEvent(new CustomEvent(constants.EVENTS.SCROLL_END, { detail: { index, handler: this } }));
        }
    }

    /**
     * add disabled property to dataset
     * @private
     */
    private disablePrev(): ScrollerNav {
        if (this.prev) {
            this.prev.dataset.disabled = this.isAtStart().toString();
        }
        return this;
    }

    /**
     * add disabled property to dataset
     * @private
     */
    private disableNext(): ScrollerNav {
        if (this.next) {
            this.next.dataset.disabled = this.isAtEnd().toString();
        }
        return this;
    }

    /**
     * add out-of-bounds class to invisible or cut items
     * @private
     */
    private classifyInvisible() {
        this.tiles.forEach((tile: HTMLElement) => {
            const paddingLeft = parseInt(this.style.paddingLeft);
            const paddingRight = parseInt(this.style.paddingRight);
            const visibleWidth = this.node.offsetWidth - paddingLeft - paddingRight;
            // add 1px to visibleWidth to ignore errors on certain resolutions
            const isVisible =
                tile.offsetLeft - paddingLeft < this.node.scrollLeft ||
                tile.offsetLeft - this.node.scrollLeft + tile.offsetWidth > visibleWidth + 1;
            tile.classList.toggle("out-of-bounds", isVisible);
        });
        return this;
    }

    /**
     * handle events
     * @private
     */
    private updateNavigation() {
        this.disablePrev().disableNext().classifyInvisible();
        return this;
    }

    private updateOffsets() {
        this.nodeOffset = this.node.getBoundingClientRect()[this.horizontal ? "left" : "top"];
        const tileWidth = [...this.tiles].find((t) => t.offsetWidth > 0)?.offsetWidth;
        if (tileWidth) {
            this.tiles.forEach((t, i) => {
                const offset = this.nodePadding + i * (this.gap + tileWidth);
                t.dataset.offset = offset.toString();
            });
        }
        return this;
    }

    private updateOrientation() {
        if (this.activeTile > 0) {
            const goto = this.activeTile;
            setTimeout(() => {
                this.goto(goto, "auto");
            }, 100);
        }
    }

    /**
     * initialize event listeners
     * @private
     */
    private addListeners(): ScrollerNav {
        const updateOffsetsHandler = this.updateOffsets.bind(this);
        const updateNavigationHandler = this.updateNavigation.bind(this);
        const updateOrientionHandler = this.updateOrientation.bind(this);
        this.node.addEventListener("scroll", this.scrollHandler, { passive: true });
        this.node.addEventListener(constants.EVENTS.SCROLLER_NAV_UPDATE, () => {
            updateOffsetsHandler();
            updateNavigationHandler();
        });
        this.node.addEventListener(constants.EVENTS.NAV_BEFORE_RESULT, this.handleNavBeforeResult.bind(this));
        [this.prev, this.next]
            .filter((n) => n instanceof HTMLElement)
            .forEach((btn) => {
                btn.addEventListener("pointerup", this.clickHandler);
            });
        this.allThumbs.forEach((t) => t.addEventListener("pointerup", this.thumbHandler));
        window.addEventListener("resize", updateOffsetsHandler);
        window.addEventListener("resize", updateNavigationHandler);
        if (!screen.orientation) {
            window.addEventListener("orientationchange", updateOrientionHandler);
        } else {
            screen.orientation.addEventListener("change", updateOrientionHandler);
        }
        return this;
    }

    private handleNavBeforeResult(e: ScrollerNavBeforeResultEvent) {
        if (e.detail?.deferNav && !isNaN(e.detail.deferNav)) {
            this.deferNav = e.detail.deferNav;
        }
    }

    /**
     * obtain top/left offset by tile index
     * @param index
     * @private
     */
    private calculateOffset(index: number): { top: number; left: number } {
        const top = this.horizontal ? 0 : this.tiles[index]?.offsetTop - parseInt(this.style.paddingTop);
        const left = this.horizontal ? this.tiles[index]?.offsetLeft - parseInt(this.style.paddingLeft) : 0;
        return { top, left };
    }

    /**
     * handle nav click
     * @param {PointerEvent} e
     * @private
     */
    private handleClick(e: PointerEvent): void {
        e.preventDefault();
        e.stopPropagation();
        const btn = e.currentTarget as HTMLElement;
        // give other libs the opportunity to change nav behavior
        this.node.dispatchEvent(new CustomEvent(constants.EVENTS.NAV_BEFORE, { detail: { handler: this } }));
        setTimeout(() => {
            const current = this.current;
            if (current > -1) {
                const index = clamp(current + (btn === this.next ? 1 : -1), 0, this.tiles.length - 1);
                this.goto(index);
                this.node.dispatchEvent(
                    new CustomEvent(constants.EVENTS.NAV_START, { detail: { handler: this, index } })
                );
            }
            this.deferNav = 0;
        }, this.deferNav);
    }

    /**
     * handle thumb click
     * @param e
     * @private
     */
    private handleThumb(e: PointerEvent): void {
        const thumb = e.currentTarget;
        // give other libs the opportunity to change nav behavior
        this.node.dispatchEvent(new CustomEvent(constants.EVENTS.THUMB_BEFORE, { detail: { handler: this } }));
        setTimeout(() => {
            const index = this.thumbs.findIndex((i) => i === thumb);
            this.goto(index);
            this.node.dispatchEvent(
                new CustomEvent(constants.EVENTS.THUMB_START, { detail: { handler: this, index } })
            );
            this.deferNav = 0;
        }, this.deferNav);
    }

    /**
     * handle scroll event
     * idea: replace with scroll timeline
     * @private
     */
    private handleScroll(): void {
        if (this.debounceScrollHandlerTimeout) {
            clearTimeout(this.debounceScrollHandlerTimeout);
            this.debounceScrollHandlerTimeout = null;
        }
        const current = this.current;
        if (current < 0) return;
        if (this._moving === false) {
            this._moving = true;
            this.node.dispatchEvent(
                new CustomEvent(constants.EVENTS.SCROLL_START, {
                    detail: {
                        handler: this,
                        index: current,
                        dir: this._pos < this.node.scrollLeft
                    }
                })
            );
        }
        this.debounceScrollHandlerTimeout = setTimeout(() => {
            clearTimeout(this.debounceScrollHandlerTimeout);
            this.updateNavigation();
            this.node.dispatchEvent(
                new CustomEvent(constants.EVENTS.SCROLL_END, {
                    detail: {
                        handler: this,
                        index: current
                    }
                })
            );
            this._moving = false;
            this._pos = this.node.scrollLeft;
            this.activeTile = current;
        }, 100);
    }
}

const nodes = document.querySelectorAll(constants.SELECTORS.SCROLLER) as NodeListOf<HTMLElement>;
nodes.forEach((scroller) => new ScrollerNav(scroller));

export default ScrollerNav;
